import { useMemo, useState } from "react";
import { Navigate, createFileRoute } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useQuery } from "@tanstack/react-query";
import { Plus, Filter } from "lucide-react";

import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { cn } from "@/lib/utils";
import { useIsAdmin } from "@/hooks/useIsAdmin";

import { JournalEntryModal } from "@/components/journal/JournalEntryModal";
import { NewAccountModal } from "@/components/journal/NewAccountModal";
import {
  getJournalEntries,
  getJournalStats,
  listTradingAccounts,
  OUTCOMES,
  type JournalEntry,
} from "@/lib/journal.functions";

export const Route = createFileRoute("/_authenticated/dashboard/journal")({
  head: () => ({
    meta: [
      { title: "Trading Journal | XAU Pro Academy" },
      {
        name: "description",
        content:
          "Operational trading log for XAU Pro members. Track every position, plan, and outcome across personal and funded accounts.",
      },
    ],
  }),
  component: JournalPage,
});

function JournalPage() {
  // Journal is WIP and admin-only: subscribers are redirected to the Command Center.
  const { isAdmin, isLoading: adminLoading } = useIsAdmin();
  const [modalOpen, setModalOpen] = useState(false);
  const [newAccountOpen, setNewAccountOpen] = useState(false);
  const [accountId, setAccountId] = useState<string>("__all");
  const [outcomeFilter, setOutcomeFilter] = useState<string>("__all");

  const fetchAccounts = useServerFn(listTradingAccounts);
  const fetchEntries = useServerFn(getJournalEntries);
  const fetchStats = useServerFn(getJournalStats);

  const { data: accountsData } = useQuery({
    queryKey: ["journal", "accounts"],
    queryFn: () => fetchAccounts(),
    staleTime: 60_000,
  });
  const accounts = accountsData?.accounts ?? [];

  const accountParam = accountId === "__all" ? undefined : accountId;
  const outcomeParam =
    outcomeFilter === "__all"
      ? undefined
      : (outcomeFilter as JournalEntry["outcome"]);

  const { data: entriesData, isLoading: entriesLoading } = useQuery({
    queryKey: ["journal", "entries", accountParam, outcomeParam],
    queryFn: () =>
      fetchEntries({
        data: { account_id: accountParam, outcome: outcomeParam, limit: 100 },
      }),
  });
  const entries = entriesData?.entries ?? [];

  const { data: statsData } = useQuery({
    queryKey: ["journal", "stats", accountParam],
    queryFn: () => fetchStats({ data: { account_id: accountParam } }),
    staleTime: 30_000,
  });
  const stats = statsData ?? {
    total_trades: 0,
    closed_trades: 0,
    open_trades: 0,
    win_rate: 0,
    total_pnl: 0,
    avg_rr: 0,
  };

  const openEntries = useMemo(
    () => entries.filter((e) => e.outcome === "still_open"),
    [entries],
  );

  if (!adminLoading && !isAdmin) return <Navigate to="/dashboard" replace />;

  const activeAccount = accounts.find((a) => a.id === accountId);
  const isFunded =
    activeAccount?.type === "funded_challenge" ||
    activeAccount?.type === "funded_live";
  const hasFundedAccount = accounts.some(
    (a) => a.type === "funded_challenge" || a.type === "funded_live",
  );
  const showFundedHint =
    !hasFundedAccount &&
    (accountId === "__all" || activeAccount?.type === "personal");

  const noFiltersApplied = accountId === "__all" && outcomeFilter === "__all";
  const showColdStart =
    !entriesLoading && entries.length === 0 && noFiltersApplied;


  return (
    <div className="container mx-auto max-w-7xl px-4 py-8">
      {/* Top strip */}
      <div className="mb-6 flex flex-col gap-3 md:flex-row md:items-end md:justify-between">
        <div>
          <div className="text-[10px] font-bold uppercase tracking-[0.18em] text-gold">
            Trading Journal
          </div>
          <h1 className="mt-1 font-display text-2xl font-bold">Operating Log</h1>
          <p className="mt-1 text-xs text-muted-foreground">
            Track executions, setups, and performance across accounts.
          </p>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          <Select value={accountId} onValueChange={setAccountId}>
            <SelectTrigger className="w-[220px]">
              <SelectValue placeholder="All accounts" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="__all">All accounts</SelectItem>
              {accounts.map((a) => (
                <SelectItem key={a.id} value={a.id}>
                  {a.name} · {a.type.replace("_", " ")}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setNewAccountOpen(true)}
            className="text-xs text-muted-foreground hover:text-foreground"
          >
            <Plus className="h-3.5 w-3.5" /> New account
          </Button>
          <Button variant="gold" onClick={() => setModalOpen(true)}>
            <Plus className="h-4 w-4" /> New entry
          </Button>
        </div>
      </div>

      {/* Funded header */}
      {isFunded && activeAccount && (
        <div className="mb-5 grid grid-cols-2 gap-3 rounded-2xl border border-gold/30 bg-card/40 p-4 backdrop-blur-xl md:grid-cols-4">
          <FundedStat
            label="Daily DD limit"
            value={fmtMoney(activeAccount.daily_dd_limit)}
          />
          <FundedStat label="Max DD limit" value={fmtMoney(activeAccount.max_dd_limit)} />
          <FundedStat label="Profit target" value={fmtMoney(activeAccount.profit_target)} />
          <FundedStat
            label="Net PnL"
            value={`${stats.total_pnl >= 0 ? "+" : ""}${stats.total_pnl.toFixed(0)}`}
            accent={stats.total_pnl >= 0 ? "emerald" : "destructive"}
          />
        </div>
      )}

      {showFundedHint && (
        <div className="mb-5 flex items-center justify-between rounded-xl border border-border/60 bg-card/30 px-4 py-2.5 text-xs text-muted-foreground backdrop-blur-xl">
          <span>
            Tracking a prop firm or funded challenge? Add a funded account to see DD limits and profit target.
          </span>
          <button
            type="button"
            onClick={() => setNewAccountOpen(true)}
            className="font-mono text-[10px] font-bold uppercase tracking-wider text-gold hover:text-gold/80"
          >
            + Funded account
          </button>
        </div>
      )}


      {/* Stat cards */}
      <div className="mb-6 grid grid-cols-2 gap-3 md:grid-cols-4">
        <StatCard
          label="Total trades"
          value={stats.total_trades.toString()}
          hint={showColdStart ? "Awaiting first entry" : undefined}
        />
        <StatCard label="Win rate" value={`${stats.win_rate.toFixed(0)}%`} />
        <StatCard
          label="Net PnL"
          value={`${stats.total_pnl >= 0 ? "+" : ""}${stats.total_pnl.toFixed(0)}`}
          accent={stats.total_pnl >= 0 ? "emerald" : "destructive"}
        />
        <StatCard label="Avg RR" value={stats.avg_rr.toFixed(2)} />
      </div>


      {/* Main grid: ledger + right rail */}
      <div className="grid gap-6 lg:grid-cols-[1fr_280px]">
        <div className="overflow-hidden rounded-2xl border border-border/60 bg-card/40 backdrop-blur-xl">
          <div className="flex items-center justify-between border-b border-border/60 px-4 py-3">
            <div className="font-mono text-[10px] font-bold uppercase tracking-widest text-muted-foreground">
              Ledger
            </div>
            <div className="flex items-center gap-2">
              <Filter className="h-3.5 w-3.5 text-muted-foreground" />
              <Select value={outcomeFilter} onValueChange={setOutcomeFilter}>
                <SelectTrigger className="h-8 w-[160px] text-xs">
                  <SelectValue placeholder="All outcomes" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__all">All outcomes</SelectItem>
                  {OUTCOMES.map((o) => (
                    <SelectItem key={o} value={o}>
                      {o.replace("_", " ")}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="border-border/60 hover:bg-transparent">
                  <Th>Date</Th>
                  <Th>Asset</Th>
                  <Th>Dir</Th>
                  <Th>Entry</Th>
                  <Th>Exit</Th>
                  <Th>RR</Th>
                  <Th>Outcome</Th>
                  <Th>Source</Th>
                </TableRow>
              </TableHeader>
              <TableBody>
                {entriesLoading && (
                  <TableRow className="border-border/60">
                    <TableCell colSpan={8} className="py-10 text-center text-xs text-muted-foreground">
                      Loading entries.
                    </TableCell>
                  </TableRow>
                )}
                {!entriesLoading && entries.length === 0 && !showColdStart && (
                  <TableRow className="border-border/60">
                    <TableCell colSpan={8} className="py-10 text-center text-xs text-muted-foreground">
                      No entries match these filters.
                    </TableCell>
                  </TableRow>
                )}
                {showColdStart && <GhostRows />}
                {entries.map((e) => (

                  <TableRow key={e.id} className="border-border/60">
                    <TableCell className="font-mono text-xs text-muted-foreground">
                      {fmtDate(e.created_at)}
                    </TableCell>
                    <TableCell className="font-display text-sm font-semibold">
                      {e.asset}
                    </TableCell>
                    <TableCell>
                      <span
                        className={cn(
                          "rounded-md px-2 py-0.5 font-mono text-[10px] font-bold uppercase",
                          e.direction === "buy"
                            ? "bg-emerald-muted text-emerald"
                            : "bg-destructive/15 text-destructive",
                        )}
                      >
                        {e.direction}
                      </span>
                    </TableCell>
                    <TableCell className="font-mono text-xs">
                      {fmtNum(e.entry_price)}
                    </TableCell>
                    <TableCell className="font-mono text-xs">
                      {fmtNum(e.exit_price)}
                    </TableCell>
                    <TableCell className="font-mono text-xs">
                      {e.rr_realized != null
                        ? e.rr_realized.toFixed(2)
                        : e.rr_planned != null
                          ? `~${e.rr_planned.toFixed(2)}`
                          : "—"}
                    </TableCell>
                    <TableCell>
                      <OutcomeBadge outcome={e.outcome} />
                    </TableCell>
                    <TableCell className="font-mono text-[10px] uppercase text-muted-foreground">
                      {e.source.replace("_", " ")}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
          {showColdStart && (
            <div className="border-t border-border/60 px-4 py-2 text-[10px] uppercase tracking-wider text-muted-foreground/70">
              Sample rows. Your logged trades will replace these.
            </div>
          )}
        </div>


        {/* Right rail */}
        <aside className="flex flex-col gap-4">
          <div className="rounded-2xl border border-border/60 bg-card/40 p-4 backdrop-blur-xl">
            <div className="mb-2 font-mono text-[10px] font-bold uppercase tracking-widest text-muted-foreground">
              Open trades
            </div>
            {openEntries.length === 0 && !showColdStart && (
              <div className="text-xs text-muted-foreground">None open.</div>
            )}
            {showColdStart && (
              <ul className="flex flex-col gap-2 opacity-40">
                <li className="flex items-center justify-between rounded-md border border-border/60 bg-slate-deep/40 px-2.5 py-1.5">
                  <div className="flex items-center gap-2">
                    <span className="h-1.5 w-1.5 rounded-full bg-emerald" />
                    <span className="font-display text-xs font-semibold">XAUUSD</span>
                    <span className="rounded-sm bg-muted px-1 font-mono text-[9px] uppercase tracking-wider text-muted-foreground">
                      sample
                    </span>
                  </div>
                  <span className="font-mono text-[10px] text-muted-foreground">2643.10</span>
                </li>
              </ul>
            )}
            {openEntries.length > 0 && (
              <ul className="flex flex-col gap-2">
                {openEntries.slice(0, 6).map((e) => (
                  <li
                    key={e.id}
                    className="flex items-center justify-between rounded-md border border-border/60 bg-slate-deep/40 px-2.5 py-1.5"
                  >
                    <div className="flex items-center gap-2">
                      <span
                        className={cn(
                          "h-1.5 w-1.5 rounded-full",
                          e.direction === "buy" ? "bg-emerald" : "bg-destructive",
                        )}
                      />
                      <span className="font-display text-xs font-semibold">{e.asset}</span>
                    </div>
                    <span className="font-mono text-[10px] text-muted-foreground">
                      {fmtNum(e.entry_price)}
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </aside>
      </div>


      <JournalEntryModal
        open={modalOpen}
        onOpenChange={setModalOpen}
        prefill={{ account_id: accountParam }}
      />

      <NewAccountModal
        open={newAccountOpen}
        onOpenChange={setNewAccountOpen}
        onCreated={(acc) => setAccountId(acc.id)}
      />
    </div>
  );
}

function Th({ children }: { children: React.ReactNode }) {
  return (
    <TableHead className="font-mono text-[10px] uppercase tracking-wider text-muted-foreground">
      {children}
    </TableHead>
  );
}

function StatCard({
  label,
  value,
  accent,
  hint,
}: {
  label: string;
  value: string;
  accent?: "emerald" | "destructive";
  hint?: string;
}) {
  const color =
    accent === "emerald"
      ? "text-emerald"
      : accent === "destructive"
        ? "text-destructive"
        : "text-foreground";
  return (
    <div className="rounded-2xl border border-border/60 bg-card/40 p-4 backdrop-blur-xl">
      <div className="text-[10px] font-semibold uppercase tracking-widest text-muted-foreground">
        {label}
      </div>
      <div className={cn("mt-1 font-mono text-xl font-bold tabular-nums", color)}>
        {value}
      </div>
      {hint && (
        <div className="mt-1 text-[10px] uppercase tracking-wider text-muted-foreground/70">
          {hint}
        </div>
      )}
    </div>
  );
}

const GHOST_ROWS = [
  { date: "Nov 12", asset: "XAUUSD", dir: "buy" as const, entry: "2643.10", exit: "2651.40", rr: "+2.1", outcome: "hit_tp" as const },
  { date: "Nov 13", asset: "XAUUSD", dir: "sell" as const, entry: "2655.80", exit: "2658.20", rr: "-1.0", outcome: "hit_sl" as const },
];

function GhostRows() {
  return (
    <>
      {GHOST_ROWS.map((g, i) => (
        <TableRow key={`ghost-${i}`} className="border-border/60 opacity-40">
          <TableCell className="font-mono text-xs text-muted-foreground">{g.date}</TableCell>
          <TableCell className="font-display text-sm font-semibold">{g.asset}</TableCell>
          <TableCell>
            <span
              className={cn(
                "rounded-md px-2 py-0.5 font-mono text-[10px] font-bold uppercase",
                g.dir === "buy" ? "bg-emerald-muted text-emerald" : "bg-destructive/15 text-destructive",
              )}
            >
              {g.dir}
            </span>
          </TableCell>
          <TableCell className="font-mono text-xs">{g.entry}</TableCell>
          <TableCell className="font-mono text-xs">{g.exit}</TableCell>
          <TableCell className="font-mono text-xs">{g.rr}R</TableCell>
          <TableCell>
            <OutcomeBadge outcome={g.outcome} />
          </TableCell>
          <TableCell className="font-mono text-[10px] uppercase text-muted-foreground">
            sample
          </TableCell>
        </TableRow>
      ))}
    </>
  );
}


function FundedStat({
  label,
  value,
  accent,
}: {
  label: string;
  value: string;
  accent?: "emerald" | "destructive";
}) {
  const color =
    accent === "emerald"
      ? "text-emerald"
      : accent === "destructive"
        ? "text-destructive"
        : "text-foreground";
  return (
    <div>
      <div className="text-[10px] font-semibold uppercase tracking-widest text-muted-foreground">
        {label}
      </div>
      <div className={cn("mt-1 font-mono text-sm font-bold tabular-nums", color)}>
        {value}
      </div>
    </div>
  );
}

function OutcomeBadge({ outcome }: { outcome: JournalEntry["outcome"] }) {
  const map: Record<JournalEntry["outcome"], string> = {
    hit_tp: "bg-emerald-muted text-emerald",
    hit_sl: "bg-destructive/15 text-destructive",
    be: "bg-muted text-muted-foreground",
    manual_close: "bg-card text-foreground border border-border/60",
    still_open: "bg-gold-muted text-gold",
  };
  return (
    <span
      className={cn(
        "rounded-md px-2 py-0.5 font-mono text-[10px] font-bold uppercase",
        map[outcome],
      )}
    >
      {outcome.replace("_", " ")}
    </span>
  );
}

function fmtNum(n: number | null) {
  if (n == null) return "—";
  return n.toFixed(n > 100 ? 2 : 4);
}

function fmtMoney(n: number | null | undefined) {
  if (n == null) return "—";
  return n.toLocaleString();
}

function fmtDate(iso: string) {
  const d = new Date(iso);
  return d.toLocaleDateString(undefined, { month: "short", day: "2-digit" });
}
